import { Button, Modal, Spin } from 'antd';
import React, { createContext, useContext, useState } from 'react';

// Interfejs wartości kontekstu
interface DocumentModalContextValues {
  showDocumentModal: (link: string) => void;
  closeModal: () => void;
}

// Stworzenie kontekstu
const DocumentModalContext = createContext<DocumentModalContextValues>(null);

// Provider dla DocumentModalContext
export const DocumentModalProvider: React.FC = ({ children }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [documentLink, setDocumentLink] = useState<string | null>(null);

  // Funkcja do wyświetlania modala z dokumentem
  const showDocumentModal = (link: string) => {
    setDocumentLink(link);
    setIsModalVisible(true);
  };

  // Funkcja do zamykania modala
  const closeModal = () => {
    setIsModalVisible(false);
    setDocumentLink(null);
  };

  return (
    <DocumentModalContext.Provider value={{ showDocumentModal, closeModal }}>
      {children}
      <DocumentModal isVisible={isModalVisible} link={documentLink} onClose={closeModal} />
    </DocumentModalContext.Provider>
  );
};

const handleDownloadFile = async (url: string) => {
  let fileName = url.split('%2F').pop() || 'plik.pdf';

  if (fileName.includes('?')) {
    fileName = fileName.split('?')[0];
  }

  try {
    const response = await fetch(url);
    const blob = await response.blob();

    // Tworzenie linku do pobrania pliku
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName; // Nazwa pliku po pobraniu
    link.click();
  } catch (error) {
    console.error('Error downloading file: ', error);
  }
};

// Komponent modal do wyświetlania dokumentów
interface DocumentModalProps {
  isVisible: boolean;
  link: string | null;
  onClose: () => void;
}

const DocumentModal: React.FC<DocumentModalProps> = ({ isVisible, link, onClose }) => {
  const [loadingState, setLoadingState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [iframeKey, setIframeKey] = useState(0);

  // Reset stan przy zmianie linku lub otwarciu modalu
  React.useEffect(() => {
    if (isVisible && link) {
      setLoadingState('loading');
      setIframeKey((prev) => prev + 1);

      // Logowanie informacji diagnostycznych
      console.log('[DocumentModal] Attempting to load PDF:', {
        url: link,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
      });

      // Fallback - jeśli po 2 sekundach nadal loading, zakładamy sukces
      // (niektóre przeglądarki nie wywołują onLoad dla PDF w iframe)
      const successFallback = setTimeout(() => {
        setLoadingState((prev) => {
          if (prev === 'loading') {
            console.log('[DocumentModal] Assuming success after 2s (fallback for PDF loading)');
            return 'success';
          }
          return prev;
        });
      }, 2000);

      // Timeout dla błędu - 15 sekund
      const errorTimeout = setTimeout(() => {
        setLoadingState((prev) => {
          if (prev === 'loading') {
            console.error('[DocumentModal] Loading timeout after 15 seconds');
            return 'error';
          }
          return prev;
        });
      }, 15000);

      return () => {
        clearTimeout(successFallback);
        clearTimeout(errorTimeout);
      };
    } else if (!isVisible) {
      // Reset do idle gdy modal jest zamknięty
      setLoadingState('idle');
    }
  }, [isVisible, link]);

  const handleIframeLoad = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    console.log('[DocumentModal] iframe onLoad event fired');

    // Sprawdzamy czy iframe rzeczywiście załadował treść
    try {
      const iframe = e.currentTarget;
      // Dla PDF z Firebase Storage, iframe może nie mieć dostępu do contentDocument
      // ale event onLoad powinien się wywołać
      console.log('[DocumentModal] iframe loaded successfully', {
        src: iframe.src,
        readyState: (iframe as any).readyState,
      });
      setLoadingState('success');
    } catch (error) {
      console.error('[DocumentModal] Error checking iframe content:', error);
      // Nawet jeśli nie możemy sprawdzić zawartości, zakładamy sukces jeśli onLoad się wywołał
      setLoadingState('success');
    }
  };

  const handleIframeError = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
    console.error('[DocumentModal] iframe loading error:', {
      error: e,
      url: link,
      timestamp: new Date().toISOString(),
    });
    setLoadingState('error');
  };

  const handleOpenInNewTab = () => {
    if (link) {
      console.log('[DocumentModal] Opening PDF in new tab:', link);
      window.open(link, '_blank', 'noopener,noreferrer');
    }
  };

  const handleRetry = () => {
    console.log('[DocumentModal] Retrying to load PDF');
    setLoadingState('loading');
    setIframeKey((prev) => prev + 1);
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      width="60%"
      style={{ height: '95vh', padding: 0 }}
      centered
      title="Podgląd Dokumentu"
      footer={
        <div className="flex justify-center gap-2">
          <Button
            onClick={(e) => {
              handleDownloadFile(link);
              e.stopPropagation();
            }}
            className="underline"
            rel="noreferrer">
            Pobierz
          </Button>
          <Button onClick={handleOpenInNewTab} type="primary">
            Otwórz w nowej karcie
          </Button>
        </div>
      }>
      {link && (
        <div style={{ height: '85vh', width: '100%', position: 'relative' }}>
          {/* Loading spinner */}
          {loadingState === 'loading' && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}>
              <Spin size="large" />
              <div style={{ marginTop: '10px', color: '#666' }}>Ładowanie dokumentu...</div>
            </div>
          )}

          {/* Error fallback */}
          {loadingState === 'error' && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                padding: '20px',
                textAlign: 'center',
              }}>
              <div style={{ fontSize: '48px', marginBottom: '20px' }}>📄</div>
              <h3 style={{ marginBottom: '10px' }}>Nie można wyświetlić dokumentu</h3>
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Dokument PDF nie może być wyświetlony w tym oknie. Może to być spowodowane ustawieniami przeglądarki lub
                zabezpieczeniami dokumentu.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <Button onClick={handleRetry}>Spróbuj ponownie</Button>
                <Button type="primary" onClick={handleOpenInNewTab}>
                  Otwórz w nowej karcie
                </Button>
                <Button onClick={() => handleDownloadFile(link)}>Pobierz dokument</Button>
              </div>
            </div>
          )}

          {/* iframe - always rendered but hidden when error */}
          {loadingState !== 'error' && (
            <iframe
              key={iframeKey}
              src={link}
              width="100%"
              height="100%"
              style={{
                border: 'none',
                display: 'block',
              }}
              title="Document Viewer"
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

// Hook do używania kontekstu DocumentModal
export const useDocumentModal = (): DocumentModalContextValues => {
  return useContext(DocumentModalContext);
};
